/**
 * MasterDataGeneralInfoView — list page for `general_info`.
 * 1:1 port of Access `frmMdQryGeneralInfo`. Factory Data-tab list-workspace style.
 */
import { type CSSProperties, useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import InfoIcon from '@mui/icons-material/Info'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import ConfirmDialog from '@/components/ConfirmDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { HIDE_ON_TABLET_INLINE } from '@/theme/responsive'
import {
  DataframeBody,
  ColumnFilterIcon,
  type ColumnFilterAnchorPosition,
  ColumnFilterPopover,
  distinctColumnValues,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  SearchFilterContent,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '@/hooks/usePagePermission'
import { useIsMobile } from '@/hooks/useResponsive'
import {
  useDeleteMdGeneralInfoMutation,
  useListMdGeneralInfoQuery,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataGeneralInfo } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import MasterDataGeneralInfoFormDialog from './MasterDataGeneralInfoFormDialog'
import { useMasterDataReload } from './MasterDataReloadContext'

const ALL_ROWS_PAGE_SIZE = 1_000_000
const ACCENT = '#da833c'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none',
}

type GiColKey = Extract<keyof MasterDataGeneralInfo, string>
type ColumnDef = { key: GiColKey; label: string; kind?: 'date' | 'datetime' | 'number'; numeric?: boolean; width?: number }

// Column order per Access frmMdQryGeneralInfo datasheet.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'date', label: 'Date', kind: 'date' },
  { key: 'liffe', label: 'Liffe', kind: 'number', numeric: true },
  { key: 'g2_fob_diffs', label: 'G2 FOB Diffs', kind: 'number', numeric: true },
  { key: 'currency', label: 'Currency' },
  { key: 'date_update', label: 'Updated Date', kind: 'datetime' },
  { key: 'person_update', label: 'Updated Person' },
]

// Freeze cột định danh (date) khi scroll ngang — §7.6 convert-table.
const FROZEN_KEYS: ReadonlySet<string> = new Set(['date'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { date: 130 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { date: 115 }

const ACCESS_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const formatAccessDate = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2,'0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}
const formatAccessDateTime = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${formatAccessDate(v)} ${`${d.getHours()}`.padStart(2,'0')}:${`${d.getMinutes()}`.padStart(2,'0')}`
}
// Access Format='Standard' → #,##0.00 (2 decimals cố định, right-aligned).
const formatStandard = (v: unknown): string => {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const cellValue = (row: MasterDataGeneralInfo, col: ColumnDef): string => {
  const v = row[col.key]
  if (v == null || v === '') return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'number') return formatStandard(v)
  return String(v)
}

export interface MasterDataGeneralInfoViewProps {
  /** When provided, render a "Datasets" back button + title pill (Factory Data workspace style). */
  onBackToHub?: () => void
}

export default function MasterDataGeneralInfoView({ onBackToHub }: MasterDataGeneralInfoViewProps = {}) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP

  const {
    selectedId: selectedKey,
    setSelectedId: setSelectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof MasterDataGeneralInfo> | null>(null)
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: keyof MasterDataGeneralInfo; position: ColumnFilterAnchorPosition } | null>(null)
  const [editing, setEditing] = useState<MasterDataGeneralInfo | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDel, setConfirmDel] = useState<MasterDataGeneralInfo | null>(null)
  const { getWidth, startResize } = useColumnWidths('masterdata.general-info')

  const { data, isLoading, isFetching, error, refetch } = useListMdGeneralInfoQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const [deleteGeneralInfo, { isLoading: deleting }] = useDeleteMdGeneralInfoMutation()

  const handleReload = useCallback(() => { void refetch() }, [refetch])
  useMasterDataReload(handleReload, isFetching)

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'master_data',
    prefKey: 'columns',
    subKey: 'general-info',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'masterdata.general-info',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () => baseRows.filter((r) =>
      rowMatchesSearch(r, searchTerm, TABLE_COLUMNS as never)
      && rowMatchesAllColumnFilters(r, columnFilters.filters),
    ),
    [baseRows, searchTerm, columnFilters.filters],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(
    () => (rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
    [page, rowsPerPage, sortedRows],
  )
  const selectedRow = useMemo(() => baseRows.find((r) => r.date === selectedKey) ?? null, [baseRows, selectedKey])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    searchPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const toggleSort = (key: keyof MasterDataGeneralInfo) => { setSortState((c) => toggleTableSort(c, key)); setPage(0) }

  const handleEdit = () => {
    if (!selectedRow) { snack.warning('Please choose a record!'); return }
    setEditing(selectedRow)
  }
  const handleDeleteRequest = () => {
    if (!selectedRow) { snack.warning('Please choose a record!'); return }
    setConfirmDel(selectedRow)
  }
  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try {
      await deleteGeneralInfo(confirmDel.date).unwrap()
      snack.success('General info deleted')
      if (selectedKey === confirmDel.date) setSelectedKey(null)
      setConfirmDel(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete'))
    }
  }

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết flex còn lại (parent đã bounded) thay vì clamp → bỏ gap dưới.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={handleEdit}>Edit</Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!canEdit || deleting} onClick={handleDeleteRequest}>Delete</Button>
          </Stack>
        }
      >
        {onBackToHub ? (
          <>
            <Button size="small" variant="outlined" color="inherit"
              startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={onBackToHub} sx={filterBarBtnSx}>
              Datasets
            </Button>
            <Stack direction="row" spacing={0.625} alignItems="center"
              sx={{ mx: 0.5, px: 1, py: 0.5, borderRadius: 1, backgroundColor: ACCENT, color: '#fff',
                    boxShadow: `0 1px 3px ${alpha(ACCENT, 0.45)}` }}>
              <InfoIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff',
                textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap', lineHeight: 1.1 }}>
                General Info
              </Typography>
            </Stack>
          </>
        ) : null}
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => searchPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load general info.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) ? <CircularProgress size={15} /> : null}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={searchPopover.state !== null}
          anchorEl={searchPopover.state?.anchor ?? null}
          onClose={() => searchPopover.close()}
        >
          {searchPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Date, currency, liffe, diffs..."
              onClear={() => searchPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        {/* Khi đang fetch (load đầu / Reload) → thay grid bằng BrandState. */}
        <DataframeBody loading={isLoading} fetching={isFetching}>
          {isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  // Sticky position inline (specificity 1,0,0,0 thắng MUI stickyHeader + stacking context).
                  const headerStyle: CSSProperties = {}
                  if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                  if (isFrozen) {
                    headerStyle.position = 'sticky'
                    headerStyle.top = 0
                    headerStyle.left = 0
                    headerStyle.zIndex = 100
                  }
                  return (
                    <TableCell
                      key={key}
                      className="app-table-header-cell"
                      onClick={() => toggleSort(col.key)}
                      align={col.numeric ? 'right' : 'left'}
                      style={Object.keys(headerStyle).length ? headerStyle : undefined}
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(key)}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_, row: MasterDataGeneralInfo) =>
              visibleColumns.map((col) => {
                const key = String(col.key)
                const isFrozen = FROZEN_KEYS.has(key)
                const val = cellValue(row, col)
                const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                const wrap = getWidth(key) != null // resize cột → wrap + auto-height (§7.8)
                if (wrap) {
                  return (
                    <TableCell
                      key={key}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      align={col.numeric ? 'right' : 'left'}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                    </TableCell>
                  )
                }
                return (
                  <TableCell
                    key={key}
                    className={isFrozen ? 'app-sticky-col' : undefined}
                    align={col.numeric ? 'right' : 'left'}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                  >
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 900, width: '100%' }} />,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as never,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as never,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              TableRow: (props: any) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const idx = Number((props as any)['data-item-index'])
                const row = Number.isFinite(idx) ? rows[idx] : null
                const k = row ? row.date : ''
                return (
                  <TableRow {...props} hover selected={k !== '' && k === selectedKey} sx={{ cursor: 'pointer' }} onClick={() => { if (k) toggleSelectKey(k) }} onDoubleClick={() => { if (row) setEditing(row) }} />
                )
              },
            }}
          />
          )}
        </DataframeBody>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <MasterDataGeneralInfoFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <MasterDataGeneralInfoFormDialog open={editing !== null} mode="edit" initialValue={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!confirmDel}
        title="Delete General Info"
        message={confirmDel ? `Delete general info for ${formatAccessDate(confirmDel.date)}?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}
